import React, { useEffect, useState } from "react";
import { Row, Col, Tabs, Tab } from "react-bootstrap";
import ResourceChart from "./ResourceChart";
import NoDataSkeleton from "../../../components/NoDataSkeleton";
import TooltipHelper from "../../../components/TooltipHelper";
import ResourcePie, { SliceOfPie } from "./ResourcePie";
import {
  COLOR_BED_VENT_COVID,
  COLOR_BED_VENT_NON_COVID,
  COLOR_BED_VENT_AVAILABLE,
} from "../../../GlobalColors";

interface BedVentilatorProps {
  data:
    | {
        beds: App.ResourceElement[];
        vents: App.ResourceElement[];
      }
    | undefined;
  currentFilter: App.CurrentFilter;
  dateFilter: boolean;
}

const BedVentilator = (props: BedVentilatorProps) => {
  const { data, currentFilter, dateFilter } = props;

  // need to convert the totals to percentages
  // we are doing it here versus in the useDashboardData hook
  // because we need to do so for an entire month
  const [todayBed, setTodayBed] = useState<SliceOfPie[]>();
  const [todayVent, setTodayVent] = useState<SliceOfPie[]>();

  function transformResourceRecord<K extends keyof App.ResourceElement>(
    resourceRecord: App.ResourceElement,
    total: K,
    type: "ICU Beds" | "Ventilators"
  ) {
    return Object.entries(resourceRecord)
      .map(
        (record): SliceOfPie => {
          let color = "";
          let subtitle = "";
          let name = "";
          let order = 0;
          // todo don't love this, but works for now
          switch (record[0]) {
            case "covid":
              color = COLOR_BED_VENT_COVID;
              name = "COVID Use";
              subtitle = `${type} in Use - COVID`;
              order = 3;
              break;
            case "nonCovid":
              color = COLOR_BED_VENT_NON_COVID;
              name = "Non-COVID Use";
              subtitle = `${type} in Use - Non-COVID`;
              order = 2;
              break;
            case "available":
              color = COLOR_BED_VENT_AVAILABLE;
              name = "Available";
              subtitle = `${type} Available`;
              order = 1;
              break;
            case "total":
              name = "total";
              order = 4;
              break;
            case "date":
              order = 5;
              name = "date";
          }
          return {
            name: name,
            // we always know the total value will be a number so shhhh
            value: record[1],
            color: color,
            order: order,
            subtitle: subtitle,
          };
        }
      )
      .sort((record) => -record.order);
  }

  useEffect(() => {
    if (!data) return;

    if (
      data.beds.slice().pop() !== undefined &&
      data.vents.slice().pop() !== undefined
    ) {
      setTodayBed(
        transformResourceRecord(data.beds.slice().pop()!, "total", "ICU Beds")
      );
      setTodayVent(
        transformResourceRecord(
          data.vents.slice().pop()!,
          "total",
          "Ventilators"
        )
      );
    }
  }, [data]);

  return (
    <Row>
      <Col xs={12} lg={12} className="h-100 mb-2">
        <div className="d-flex justify-content-between">
          <h3 className="mb-3">Resource Usage</h3>
          <div>
            {dateFilter ? (
              <TooltipHelper
                toolTipClass=" mt-3"
                altIcon="far fa-calendar"
                text="Filtered on Date Range"
              />
            ) : undefined}
            <TooltipHelper
              toolTipClass="mt-3"
              text="These numbers are provisional and only reflect data that has been reported to IDOH’s EM Resources database. 
                                Total capacity will change as facilities update information. Numbers should not be characterized as a comprehensive total."
            />
          </div>
        </div>
        <Tabs className="mb-3" defaultActiveKey="today" id="resource">
          <Tab eventKey="today" title="Today">
            <Row>
              <Col className="mb-3" xs={12} md={6}>
                <h5 className="text-center">{`Today's ${
                  currentFilter.filterType === "regional"
                    ? currentFilter.displayName
                    : currentFilter.regionName
                    ? currentFilter.regionName
                    : "Statewide"
                } ICU Bed Usage`}</h5>
                {todayBed ? <ResourcePie data={todayBed} /> : null}
              </Col>
              <Col xs={12} md={6}>
                <h5 className="text-center">{`Today's ${
                  currentFilter.filterType === "regional"
                    ? currentFilter.displayName
                    : currentFilter.regionName
                    ? currentFilter.regionName
                    : "Statewide"
                } Ventilator Usage`}</h5>
                {todayVent ? <ResourcePie data={todayVent} /> : null}
              </Col>
            </Row>
          </Tab>
          <Tab eventKey="daily" title="By Day">
            {data ? (
              <Row>
                <Col xs={12} lg={6}>
                  <h5>{`${
                    currentFilter.filterType === "regional"
                      ? currentFilter.displayName + "'s"
                      : currentFilter.regionName
                      ? currentFilter.regionName
                      : "Statewide"
                  } ICU Bed Usage by Day`}</h5>
                  <ResourceChart resource="ICU Beds" data={data.beds} />
                </Col>
                <Col xs={12} lg={6}>
                  <h5>{`${
                    currentFilter.filterType === "regional"
                      ? currentFilter.displayName + "'s"
                      : currentFilter.regionName
                      ? currentFilter.regionName
                      : "Statewide"
                  } Ventilator Usage by Day`}</h5>
                  <ResourceChart resource="Ventilator" data={data.vents} />
                </Col>
              </Row>
            ) : (
              <NoDataSkeleton
                message={
                  "There is no ICU bed usage data for the selected county."
                }
              />
            )}
          </Tab>
        </Tabs>
      </Col>
    </Row>
  );
};

export default BedVentilator;
